/**
 * `PortableStyles` → React Native styles.
 *
 * `PortableStyles` (core `types/config.ts`) is the deliberately small subset of
 * CSS the config surface accepts precisely so it can cross platforms. Web hands
 * the values straight to the DOM; RN cannot — it has no CSS parser, so a
 * `"16px"` string silently breaks layout instead of throwing.
 *
 * This is the one place that converts. Rules, matching the Dart port
 * (`flutter/lib/src/ui/widgets/portable_styles.dart`):
 *  - numbers pass through as density-independent pixels;
 *  - `"16px"` / `"16"` → `16`;
 *  - `"50%"` survives only where RN accepts percentages (width/height);
 *  - anything else (`calc()`, `em`, `auto`, gradients in `backgroundColor`) is
 *    dropped rather than guessed at — a dropped style degrades, a wrong one
 *    corrupts the layout.
 */

import type { ViewStyle } from "react-native";
import type { PortableStyles } from "@ringg/core";

/** RN accepts percentage strings only for these dimensions. */
type Dimension = number | `${number}%` | undefined;

const PERCENT = /^-?\d+(\.\d+)?%$/;

const isPercent = (value: string): value is `${number}%` => PERCENT.test(value.trim());

/** `"16px"`, `"16"`, `16` → `16`. Returns undefined for anything unparseable. */
const toNumber = (value: string | number | undefined): number | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  const trimmed = value.trim();
  const numeric = trimmed.endsWith("px") ? trimmed.slice(0, -2) : trimmed;
  const parsed = Number(numeric);
  return Number.isFinite(parsed) && numeric.trim() !== "" ? parsed : undefined;
};

/** Width/height: numbers and percentages both survive. */
const toDimension = (value: string | number | undefined): Dimension => {
  if (typeof value === "string" && isPercent(value)) return value.trim() as `${number}%`;
  return toNumber(value);
};

/**
 * Drop `undefined` entries so the result can be spread over a base style
 * without punching holes in it (`{...base, ...portable}` with an explicit
 * `undefined` would erase the base value).
 */
const compact = (style: ViewStyle): ViewStyle => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(style)) {
    if (value !== undefined) result[key] = value;
  }
  return result as ViewStyle;
};

/** Convert a config `PortableStyles` bag into an RN `ViewStyle`. */
export const toViewStyle = (styles: PortableStyles | undefined): ViewStyle => {
  if (!styles) return {};
  return compact({
    width: toDimension(styles.width),
    height: toDimension(styles.height),
    // Gradient strings are not valid RN colors — the gradient-aware surfaces
    // read `theme.primaryColor` through <GradientFill> instead.
    backgroundColor: typeof styles.backgroundColor === "string" && !styles.backgroundColor.includes("(") ? styles.backgroundColor : undefined,
    padding: toNumber(styles.padding),
    borderRadius: toNumber(styles.borderRadius),
  });
};

/** Icon sizing from `ButtonIconConfig.size` (same parsing, single value). */
export const toSize = (value: string | number | undefined, fallback: number): number => toNumber(value) ?? fallback;
